import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Layout, Typography, Card, Spin, Button, Row, Col } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../utils/redux/store'
import { attemptGetPlaylistsFolderList, PlaylistBrief, PlaylistOrderBy } from '../../utils/redux/playlistSlice'
import { OrderDirection } from '../../utils/dto'
import { apiBaseURL } from '../../utils/api'
import './CategoryPage.scss'

const { Content } = Layout
const { Title } = Typography

type CategoryType = 'mood' | 'style' | 'daytime' | 'business'

const folderToParam: Record<CategoryType, string> = {
  style: 'style',
  business: 'business',
  mood: 'mood',
  daytime: 'daytime',
}

export default function CategoryPage() {
  const { type, id } = useParams<{ type: CategoryType; id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation('home')
  const dispatch = useAppDispatch()
  const [playlists, setPlaylists] = useState<PlaylistBrief[]>([])
  const [loading, setLoading] = useState(true)
  const [categoryName, setCategoryName] = useState('')

  useEffect(() => {
    const fetchPlaylists = async () => {
      if (!type || !id) return

      try {
        setLoading(true)
        const result = await dispatch(
          attemptGetPlaylistsFolderList({
            skip: 0,
            limit: 200,
            oderBy: PlaylistOrderBy.TITLE,
            oderDirection: OrderDirection.DESC,
            [folderToParam[type]]: id,
          }),
        ).unwrap()

        setPlaylists(result.playlists)
      } catch (error) {
        console.error('Error fetching category playlists:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchPlaylists()
  }, [type, id, dispatch])

  const handlePlaylistClick = (playlistId: string) => {
    navigate(`/playlist/${playlistId}`)
  }

  if (loading) {
    return (
      <div className="category-loading">
        <Spin size="large" />
      </div>
    )
  }

  return (
    <Content className="category-content">
      <div className="category-container">
        <div className="category-header">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            size="large"
          />
          <Title level={2} className="category-title">
            {categoryName || t(type || '')}
          </Title>
          <div style={{ width: 40 }} />
        </div>

        <Row gutter={[16, 16]} className="category-grid">
          {playlists.map((playlist) => (
            <Col xs={12} sm={8} md={6} lg={4} key={playlist._id}>
              <Card
                hoverable
                className="category-card"
                cover={
                  <div className="category-card-image">
                    <img
                      alt={playlist.title}
                      src={`${apiBaseURL}/uploaded/playlists/${playlist.image}`}
                    />
                  </div>
                }
                onClick={() => handlePlaylistClick(playlist._id)}
              >
                <Card.Meta
                  title={playlist.title}
                  description={
                    playlist.songsCount
                      ? `${playlist.songsCount} ${t('songs', { ns: 'playlist' })}`
                      : undefined
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </Content>
  )
}
